import { memo, useEffect, useState } from 'react';

import Dropdown, { type DropdownOption } from '../../../components/Dropdown';
import RadioGroup, { type RadioOption } from '../../../components/Radio/RadioGroup';
import type { ApprovalDetailsAdditionalAccessData, PivCardSelection } from '../types';
import './ApprovalDetailsAdditionalAccessContent.css';

interface ApprovalDetailsAdditionalAccessContentProps {
  initialValue?: ApprovalDetailsAdditionalAccessData;
  onCompletionChange?: (isComplete: boolean) => void;
  onPendingChangesChange?: (hasPendingChanges: boolean) => void;
  onPivCardSelectionChange?: (value: PivCardSelection) => void;
  onWorkingSixMonthsChange?: (value: boolean) => void;
  showValidation?: boolean;
}

const WORKING_SIX_MONTHS_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
] satisfies DropdownOption<boolean | null>[];

const PIV_CARD_OPTIONS = [
  { label: 'Yes, this person will be issued a PIV card.', value: 'piv' },
  { label: 'No, this person will obtain logical access using a PIV alternative.', value: 'remote_rla' },
] satisfies RadioOption<PivCardSelection>[];

const EXTERNAL_FEDERAL_PIV_OPTION = {
  label: 'This person has a PIV from another agency.',
  value: 'external_federal_piv',
} satisfies RadioOption<PivCardSelection>;

const SIX_MONTHS_TOOLTIP =
  '“Six Months or Less” refers to a Contractor who works for six months or less, formerly known as a “seasonal” contractor. Such Contractors must reapply at the end of the contract. Switching from “Yes” to “No” requires full vetting, whereas switching from “No” to “Yes” does not.';

const PIV_ISSUANCE_TOOLTIP =
  'Applicants who need routine unescorted access to CMS facilities may need a PIV card. Please consult the Contracting Officer for the contract to obtain information on travel costs. If no PIV card is issued, the applicant must be escorted by their Federal Approver or a designee.';

function ApprovalDetailsAdditionalAccessContentComponent({
  initialValue,
  onCompletionChange,
  onPendingChangesChange,
  onPivCardSelectionChange,
  onWorkingSixMonthsChange,
  showValidation = false,
}: ApprovalDetailsAdditionalAccessContentProps) {
  const baseTestId = 'approval-details-additional-access-content';

  const initialWorkingSixMonths = initialValue?.workingSixMonths ?? null;
  const initialPivCardSelection = initialValue?.pivCardSelection ?? null;
  const hasExternalFederalPiv = initialValue?.hasExternalFederalPiv ?? false;
  // ACP-2438: the question is only asked on a contract configured for temporary or
  // seasonal users. Elsewhere it is not rendered and the mapper has already settled
  // the answer as No, so completion and the approve payload need no special case.
  const allowsTemporarySeasonal = initialValue?.allowsTemporarySeasonal !== false;
  const persistedPivCardSelection = initialValue?.persistedPivCardSelection ?? null;
  // The selection cannot change when the applicant holds an external federal PIV or when a
  // prior COR approval already persisted the credential decision (re-approval pass).
  const pivSelectionLocked = hasExternalFederalPiv || persistedPivCardSelection !== null;

  const [workingSixMonths, setWorkingSixMonths] = useState<boolean | null>(initialWorkingSixMonths);
  const [pivCardSelection, setPivCardSelection] = useState<PivCardSelection | null>(initialPivCardSelection);

  const workingSixMonthsError = showValidation && workingSixMonths === null ? 'You must make a choice.' : '';
  const pivCardSelectionError = showValidation && pivCardSelection === null ? 'You must make a choice.' : '';
  const isComplete = workingSixMonths !== null && pivCardSelection !== null;

  let validationMessage = '';

  if (showValidation) {
    const errors = [
      ...(workingSixMonths === null ? ['Working six months or less'] : []),
      ...(pivCardSelection === null ? ['PIV issuance'] : []),
    ];
    const errorCount = errors.length;
    validationMessage =
      errorCount > 0
        ? `${errorCount} required ${errorCount === 1 ? 'field' : 'fields'} ${
            errorCount === 1 ? 'is' : 'are'
          } incomplete: ${errors.join(', ')}`
        : 'All required fields are complete';
  }

  useEffect(() => {
    onCompletionChange?.(isComplete);
  }, [isComplete, onCompletionChange]);

  useEffect(() => {
    const hasPendingChanges =
      workingSixMonths !== initialWorkingSixMonths || pivCardSelection !== initialPivCardSelection;
    onPendingChangesChange?.(hasPendingChanges);
  }, [initialPivCardSelection, initialWorkingSixMonths, onPendingChangesChange, pivCardSelection, workingSixMonths]);

  const availableOptions =
    hasExternalFederalPiv || persistedPivCardSelection === 'external_federal_piv'
      ? [...PIV_CARD_OPTIONS, EXTERNAL_FEDERAL_PIV_OPTION]
      : PIV_CARD_OPTIONS;
  const pivCardOptions = pivSelectionLocked
    ? availableOptions.map((option) => ({ ...option, disabled: true }))
    : availableOptions;

  return (
    <div
      className='approval-details-additional-access-content'
      data-testid={baseTestId}>
      {/* ACP-2092: mounted up front and only its text changes — a live region created
          together with its content is announced inconsistently or not at all. */}
      <div
        aria-atomic='true'
        aria-live='polite'
        className='ds-u-visibility--screen-reader'
        role='status'>
        {validationMessage}
      </div>
      <p className='approval-details-additional-access-content__intro'>
        Please fill out the following for this person.
      </p>

      {allowsTemporarySeasonal && (
        <div className='approval-details-additional-access-content__field approval-details-additional-access-content__field--dropdown'>
          <Dropdown
            required
            data-testid={`${baseTestId}-working-six-months`}
            errorMessage={workingSixMonthsError}
            label='Will the applicant be working on this Contract for six (6) months or less?'
            name='working-six-months'
            options={WORKING_SIX_MONTHS_OPTIONS}
            tooltipText={SIX_MONTHS_TOOLTIP}
            value={workingSixMonths}
            onChange={(value) => {
              setWorkingSixMonths(value);
              if (value !== null) {
                onWorkingSixMonthsChange?.(value);
              }
            }}
          />
        </div>
      )}

      <div className='approval-details-additional-access-content__field'>
        <RadioGroup<PivCardSelection>
          required
          data-testid={`${baseTestId}-requires-piv-card`}
          errorMessage={pivCardSelectionError}
          label='Will the applicant require routine access to any CMS facility OR require a PIV card to log into any CMS systems or applications?'
          name='requires-piv-card'
          options={pivCardOptions}
          tooltipText={PIV_ISSUANCE_TOOLTIP}
          value={pivCardSelection ?? undefined}
          onChange={(value) => {
            if (pivSelectionLocked) {
              return;
            }

            setPivCardSelection(value);
            onPivCardSelectionChange?.(value);
          }}
        />
      </div>
    </div>
  );
}

const ApprovalDetailsAdditionalAccessContent = memo(ApprovalDetailsAdditionalAccessContentComponent);

export default ApprovalDetailsAdditionalAccessContent;
